import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, exhaustMap, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { UserAccountsApiService } from './user-accounts-api.service'
import * as UserAccountsActions from './user-accounts.actions'
import { UserAccountsFacade } from './user-accounts.facade'
import { UserAccountsEntity } from './user-accounts.models'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'
import { GbDashboardDialogService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-dialog-service/gb-dashboard-dialog.service'
import { GbArchiveUserDialogComponent } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-archive-user-dialog/gb-archive-user-dialog.component'

@Injectable()
export class UserAccountsEffects {
  private actions$ = inject(Actions)

  loadUserAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.loadUserAccounts),
      withLatestFrom(this.userAccountsFacade.allUserAccounts$),
      switchMap(([action, allUserAccounts]) => {
        let offset = allUserAccounts.length || 0
        action.shouldReset ? (offset = 0) : (offset = offset)

        return this.userAccountsApiService.loadUserAccounts(offset, action.searchTerm, action.ordering).pipe(
          map((response: GbPaginatedResponse<UserAccountsEntity>) => {
            if (!action.shouldReset) {
              return UserAccountsActions.loadUserAccountsSuccess({
                userAccounts: response.results,
                count: response.count,
              })
            } else {
              return UserAccountsActions.setUserAccounts({ userAccounts: response.results, count: response.count })
            }
          }),
          catchError((error) => {
            return of(UserAccountsActions.loadUserAccountsFailure({ error }))
          }),
        )
      }),
    ),
  )

  openArchiveUserDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.openArchiveUserDialog),
      exhaustMap(() =>
        this.gbDashboardDialogService.getDialogObs(GbArchiveUserDialogComponent, {
          width: '500px',
        }),
      ),
      filter((res) => !!res),
      map(() => UserAccountsActions.archiveUser()),
    ),
  )

  archiveUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountsActions.archiveUser),
      withLatestFrom(this.userAccountsFacade.selectedUserAccount$),
      switchMap(([, selectedUserAccount]) =>
        this.userAccountsApiService.archiveUser(selectedUserAccount?.uuid).pipe(
          map((response: UserAccountsEntity) => UserAccountsActions.archiveUserSuccess({ userAccount: response })),
          catchError((error: HttpErrorResponse) => of(UserAccountsActions.archiveUserFailure({ error }))),
        ),
      ),
    ),
  )

  archiveUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccountsActions.archiveUserSuccess),
        tap(() => {
          this.gbDashboardDialogService.closeDialog(GbArchiveUserDialogComponent)
          this.gbNotificationService.success('USER_ACCOUNTS.ARCHIVE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccountsActions.loadUserAccountsFailure, UserAccountsActions.archiveUserFailure),
        map((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private userAccountsFacade: UserAccountsFacade,
    private userAccountsApiService: UserAccountsApiService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
  ) {}
}
